// src/features/analytics/playbackTrend.ts
//
// Pure helpers for the playback trend chart (PlaybackTrendCard). The API omits
// days with no stored data (honest absence); the chart needs a continuous UTC
// day axis, so gaps are zero-filled CLIENT-SIDE only, and only across days that
// were actually measurable — never future days, never days before coverage
// began. Day grain is UTC (the axis is labeled accordingly); day values are
// epoch seconds of the UTC midnight opening the day.

import { SECONDS_PER_DAY, type UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsCoverage, PlaybackDailyPoint } from './types';

/**
 * Clamp the requested range to the days the chart may honestly zero-fill:
 * start no earlier than coverage.from's UTC day (before it, nothing was
 * collected — zero would be a fabricated measurement) and end no later than
 * the UTC day after `nowSeconds` (future days have no data by definition;
 * presets like currentYear span the whole calendar year).
 */
export function fillableRange(
  range: UtcRange,
  coverage: AnalyticsCoverage | undefined,
  nowSeconds: number
): UtcRange {
  let from = range.from;
  if (coverage) {
    const coverageDayStart = Math.floor(coverage.from / SECONDS_PER_DAY) * SECONDS_PER_DAY;
    if (coverageDayStart > from) from = coverageDayStart;
  }
  const tomorrow = (Math.floor(nowSeconds / SECONDS_PER_DAY) + 1) * SECONDS_PER_DAY;
  const to = Math.min(range.to, tomorrow);
  return { from, to };
}

/**
 * Zero-fill missing UTC days across an end-exclusive day-aligned range so the
 * axis is continuous. Points outside the range pass through untouched at their
 * sorted position (defensive; the API only returns in-range days).
 */
export function fillDailyGaps(
  points: PlaybackDailyPoint[],
  range: UtcRange
): PlaybackDailyPoint[] {
  const byDay = new Map(points.map((p) => [p.day, p]));
  const out: PlaybackDailyPoint[] = [];
  for (let day = range.from; day < range.to; day += SECONDS_PER_DAY) {
    out.push(byDay.get(day) ?? { day, sessions: 0, watchTimeMs: 0, uniqueViewers: 0 });
  }
  return out;
}

/**
 * Preset-aware x-tick thinning: show every Nth day label so ~90-day ranges get
 * weekly ticks and year-scale ranges get ~monthly ticks (recharts XAxis
 * `interval` semantics: 0 = every tick, N = skip N between shown ticks).
 */
export function trendTickInterval(days: number): number {
  if (days <= 14) return 0;
  if (days <= 45) return 2;
  if (days <= 120) return 6;
  return 29;
}

// NOTE: no client-side range-total helper on purpose — sessions/uniqueViewers
// are uniq-state merges, so summing per-day points double-counts sessions that
// span UTC midnight (~1.6% high in prod verification). The card's range-total
// annotation uses the overview response's range-wide merge instead.
